import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Trophy, ArrowLeft, Eye, EyeOff, Mail, CheckCircle, Loader2, Briefcase, Users, GraduationCap, Shield, ShieldCheck, UserCircle2 } from 'lucide-react';
import { LOGO_URL } from '@/lib/brand';
import { toast } from 'sonner';
import api from '@/lib/api';

// Phase 111 — Workspace card metadata for the Identity Gate. Drives the
// icon + tone + descriptive copy on each role chooser card.
const ROLE_GATE_META = {
  super_admin:   { title: 'Continue as Out-Play Super Admin', subtitle: 'Manage platform tenants, leads, funnels, system health.',
                   icon: Shield,         border: 'border-rose-300',    bg: 'bg-rose-50',    iconBg: 'bg-rose-100',     iconColor: 'text-rose-700',    titleColor: 'text-rose-900' },
  academy_owner: { title: 'Continue as Academy Owner / Manager', subtitle: 'Run admissions, rosters, fees, CRM, employees.',
                   icon: Briefcase,      border: 'border-emerald-300', bg: 'bg-emerald-50', iconBg: 'bg-emerald-100',  iconColor: 'text-emerald-700', titleColor: 'text-emerald-900' },
  coach:         { title: 'Continue as Coach', subtitle: 'Mark attendance, log drills, score players, manage your batches.',
                   icon: Trophy,         border: 'border-sky-300',     bg: 'bg-sky-50',     iconBg: 'bg-sky-100',      iconColor: 'text-sky-700',     titleColor: 'text-sky-900' },
  employee:      { title: 'Continue as Academy Employee', subtitle: 'Front-desk, HR, accounting, broadcasts — your permissioned views.',
                   icon: UserCircle2,    border: 'border-violet-300',  bg: 'bg-violet-50',  iconBg: 'bg-violet-100',   iconColor: 'text-violet-700',  titleColor: 'text-violet-900' },
  student:       { title: 'Continue as Student', subtitle: 'See your schedule, progress cards, fee invoices.',
                   icon: GraduationCap,  border: 'border-amber-300',   bg: 'bg-amber-50',   iconBg: 'bg-amber-100',    iconColor: 'text-amber-700',   titleColor: 'text-amber-900' },
  parent:        { title: 'Continue as Parent', subtitle: "Track your child's attendance, fees, and progress.",
                   icon: Users,          border: 'border-pink-300',    bg: 'bg-pink-50',    iconBg: 'bg-pink-100',     iconColor: 'text-pink-700',    titleColor: 'text-pink-900' },
  default:       { title: 'Continue as User', subtitle: 'Open your workspace.',
                   icon: UserCircle2,    border: 'border-slate-300',   bg: 'bg-slate-50',   iconBg: 'bg-slate-100',    iconColor: 'text-slate-700',   titleColor: 'text-slate-900' },
};

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  // Phase 97 — Multi-role disambiguation. When the same email maps to several
  // roles (e.g. coach + parent), the backend returns 409 with the role list.
  // We surface a small inline picker right under the login form.
  const [availableRoles, setAvailableRoles] = useState([]);

  // Forgot password state
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const [forgotError, setForgotError] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(forgotEmail);

  const doLogin = async (chosenRole = null) => {
    setLoading(true);
    try {
      const userData = await login(email, password, chosenRole);
      toast.success('Welcome back!');
      // Honor ?next= redirect (set by global 401 handler)
      const params = new URLSearchParams(window.location.search);
      const nextRaw = params.get('next');
      let next = null;
      if (nextRaw) {
        try {
          const decoded = decodeURIComponent(nextRaw);
          // Only allow same-origin paths to prevent open-redirect
          if (decoded.startsWith('/') && !decoded.startsWith('//')) {
            next = decoded;
          }
        } catch (e) { /* ignore */ }
      }
      if (next) {
        navigate(next);
        return;
      }
      // Phase 99 — Universal /dashboard landing. UnifiedDashboard sub-routes
      // to the right page based on role, so every login lands cleanly.
      navigate('/dashboard');
    } catch (err) {
      // Phase 97 — Multi-role disambiguation. Backend returns 409 with a list of
      // available roles. Surface a picker so the user can choose which identity.
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.code === 'role_disambiguation_required') {
        setAvailableRoles(detail.roles || []);
        toast.info(detail.message || 'Please pick which account to sign in as.');
      } else {
        const msg = (typeof detail === 'string' ? detail : detail?.message) || 'Invalid credentials';
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please fill in all fields');
      return;
    }
    // Reset the role-picker state on a fresh submit so changing credentials
    // doesn't leave a stale role list on screen.
    setAvailableRoles([]);
    await doLogin(null);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail) {
      setForgotError('Please enter your email address');
      return;
    }
    // Real-time email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forgotEmail)) {
      setForgotError('Please enter a valid email address');
      return;
    }
    setForgotLoading(true);
    setForgotError('');
    try {
      const res = await api.post('/auth/forgot-password', { email: forgotEmail });
      setForgotSuccess(true);
      setForgotMessage(res.data.message || `If an account exists for ${forgotEmail}, a reset link has been sent.`);
    } catch (err) {
      const status = err.response?.status;
      if (status === 429) {
        setForgotError('Too many reset requests. Please try again in 1 hour.');
      } else {
        // Always show neutral success even on backend error to avoid enumeration
        setForgotSuccess(true);
        setForgotMessage(`If an account exists for ${forgotEmail}, a reset link has been sent.`);
      }
    } finally {
      setForgotLoading(false);
    }
  };

  const openForgotDialog = () => {
    setForgotEmail(email || '');
    setForgotError('');
    setForgotSuccess(false);
    setForgotOpen(true);
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4" data-testid="login-page">
      <div className="absolute inset-0 bg-[radial-gradient(1200px_circle_at_20%_0%,rgba(254,252,232,0.85)_0%,rgba(248,250,252,0.92)_42%,rgba(248,250,252,1)_70%)]" />
      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-sm transition-colors duration-150">
            <ArrowLeft className="h-4 w-4" /> Back to home
          </Link>
          <div className="flex items-center justify-center gap-2 mb-2">
            <img src={LOGO_URL} alt="Out-Play" className="h-10 w-10 rounded-lg object-contain" />
            <span className="font-display font-bold text-2xl">Out-Play</span>
          </div>
        </div>
        <Card className="border shadow-lg">
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-xl font-display">Welcome back</CardTitle>
            <CardDescription>Sign in to your academy account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" placeholder="you@academy.com"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input" autoFocus
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="password">Password</Label>
                  <button
                    type="button"
                    onClick={openForgotDialog}
                    className="text-xs text-primary hover:underline font-medium transition-colors duration-150"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <div className="relative">
                  <Input
                    id="password" type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                  />
                  <button
                    type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors duration-150"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" className="w-full" disabled={loading} data-testid="auth-login-submit-button">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Signing in...</> : 'Sign In'}
              </Button>
            </form>
            {/* Phase 111 — Identity Gate.
                Card-style workspace chooser surfaces when the same email maps
                to multiple roles. Replaces the small inline-button picker. */}
            {availableRoles.length > 0 && (
              <div className="mt-5 space-y-3" data-testid="identity-gate">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-teal-600" />
                  <p className="text-sm font-semibold text-foreground">Pick your workspace</p>
                </div>
                <p className="text-xs text-muted-foreground -mt-1">
                  This email is registered for multiple roles. Choose which one to sign in as for this session.
                </p>
                <div className="grid grid-cols-1 gap-2.5">
                  {availableRoles.map((r) => {
                    const meta = ROLE_GATE_META[r] || ROLE_GATE_META.default;
                    const Icon = meta.icon;
                    return (
                      <button
                        key={r}
                        type="button"
                        disabled={loading}
                        onClick={() => doLogin(r)}
                        className={`group relative w-full text-left rounded-xl border-2 ${meta.border} ${meta.bg} hover:shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:hover:translate-y-0 px-4 py-3.5 flex items-center gap-3`}
                        data-testid={`identity-gate-card-${r}`}
                      >
                        <div className={`shrink-0 h-10 w-10 rounded-lg ${meta.iconBg} flex items-center justify-center`}>
                          <Icon className={`h-5 w-5 ${meta.iconColor}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-semibold ${meta.titleColor}`}>{meta.title}</p>
                          <p className="text-[11px] text-slate-600 mt-0.5">{meta.subtitle}</p>
                        </div>
                        <span className="text-xs font-medium text-slate-500 group-hover:text-slate-900 transition-colors">
                          {loading ? '…' : 'Enter →'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            <p className="text-center text-sm text-muted-foreground mt-4">
              Don't have an account?{' '}
              <Link to="/signup" className="text-primary font-medium hover:underline" data-testid="login-signup-link">Sign up</Link>
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md" data-testid="forgot-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Reset Password</DialogTitle>
            <DialogDescription>
              Enter your email address and we'll send you a link to reset your password.
            </DialogDescription>
          </DialogHeader>

          {forgotSuccess ? (
            <div className="py-6 text-center space-y-4" data-testid="forgot-password-success">
              <div className="h-14 w-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto">
                <CheckCircle className="h-7 w-7 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-base font-semibold">Check your email</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {forgotMessage || `If an account exists for ${forgotEmail}, a reset link has been sent.`}
                </p>
                <p className="text-xs text-muted-foreground mt-2">From <strong>contact@out-play.in</strong> · expires in 60 minutes</p>
              </div>
              <p className="text-xs text-muted-foreground">Didn't receive it? Check your spam folder or try again.</p>
              <Button variant="outline" onClick={() => { setForgotSuccess(false); setForgotOpen(false); }} className="w-full" data-testid="forgot-password-done-button">
                Back to Login
              </Button>
            </div>
          ) : (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="forgot-email" className="text-sm">Email address</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@academy.com"
                    value={forgotEmail}
                    onChange={(e) => { setForgotEmail(e.target.value); setForgotError(''); }}
                    className={`pl-9 ${forgotEmail && !emailValid ? 'border-amber-400 focus-visible:ring-amber-400' : ''}`}
                    data-testid="forgot-password-email-input"
                    autoFocus
                  />
                  {forgotEmail && emailValid && (
                    <CheckCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-emerald-500" data-testid="forgot-email-valid-icon" />
                  )}
                </div>
                {forgotEmail && !emailValid && (
                  <p className="text-xs text-amber-600" data-testid="forgot-email-format-warning">Please enter a valid email address</p>
                )}
              </div>
              {forgotError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm" data-testid="forgot-password-error">
                  <span className="text-xs font-medium">{forgotError}</span>
                </div>
              )}
              <Button type="submit" className="w-full" disabled={forgotLoading || !emailValid} data-testid="forgot-password-submit-button">
                {forgotLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sending...</> : 'Send Reset Link'}
              </Button>
              <button
                type="button"
                onClick={() => setForgotOpen(false)}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors duration-150"
              >
                Back to login
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
